import type { TOC } from '@ember/component/template-only';
import { on } from '@ember/modifier';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import { and, eq, gt } from 'ember-truth-helpers';
import TableDrawer from 'garaje/components/table-drawer';

interface DetailEvent {
  eventId: string;
  processedDateDisplay: string;
  processedTimeDisplay: string;
  integrationName: string;
  locationName: string | null;
  operationType: string;
  operationTypeLabel: string;
  operationDefinition: string | null;
  operationStatus: string;
  failureType: string | null;
  failureReason: string | null;
  errorDetail: string | null;
  recommendedAction: string | null;
  helpCenterUrl: string | null;
  responsibleParty: string | null;
  direction: string | null;
  endpoint: string | null;
  durationMillis: number;
  durationDisplay: string;
  primaryMetric: string | null;
  additionalMetrics: { name: string; value: number }[] | null;
  correlationId: string | null;
  contextDetails: Record<string, string> | null;
}

interface HealthDashboardEventDetailDrawerSignature {
  Args: {
    event: DetailEvent;
    onClose: () => void;
    onCloseAll?: () => void;
    relatedEventsLoading: boolean;
    relatedEventsCount: number;
    onOpenRelatedDrawer: () => void;
    showBackArrow?: boolean;
    eventLogUrl?: string;
  };
}

// prettier-ignore
export default <template>
  {{! template-lint-disable }}
  <TableDrawer>
    {{! Header — grey background }}
    <div class="bg-gray-50 px-6 py-5">
      <div class="flex justify-between items-start">
        <div class="flex items-start gap-3 min-w-0">
          {{#if @showBackArrow}}
            <button type="button" class="text-carbon-40 hover:text-carbon-70 mt-1 shrink-0" {{on "click" @onClose}}>
              {{svgJar "chevron-grey-left" height="14" width="14"}}
            </button>
          {{/if}}
          <div class="min-w-0">
            <h3 class="text-base font-semibold mb-0.5">{{@event.integrationName}}</h3>
            <p class="text-xs text-carbon-40 mb-0.5">{{@event.processedDateDisplay}}
              &bull;
              {{@event.processedTimeDisplay}}</p>
            <p class="text-sm text-carbon-50 mb-0">{{if @event.locationName @event.locationName "–"}}</p>
          </div>
        </div>
        <button
          type="button"
          class="border border-gray-300 rounded-md p-1 text-carbon-40 hover:text-carbon-70 hover:bg-white shrink-0 ml-4"
          {{on "click" (if @onCloseAll @onCloseAll @onClose)}}
        >
          {{svgJar "cross-black" height="14" width="14"}}
        </button>
      </div>

      {{! Status + Duration cards }}
      <div class="flex gap-3 mt-4">
        <div class="flex-1 bg-white border border-gray-200 rounded-lg px-4 py-3">
          <p class="text-xs text-carbon-40 mb-1">Status</p>
          {{#if (eq @event.operationStatus "FAILED")}}
            {{#if (eq @event.failureType "TIMEOUT")}}
              <span class="flex items-center gap-1 text-yellow-600 font-medium text-sm">
                <div class="shrink-0 w-4 h-4">{{svgJar "caution-triangle" height="14" width="14"}}</div>
                Timeout
              </span>
            {{else}}
              <span class="flex items-center gap-1 text-red-600 font-medium text-sm">
                <div class="shrink-0 w-4 h-4">{{svgJar "circle-cross-red" height="14" width="14"}}</div>
                Failed
              </span>
            {{/if}}
          {{else if (eq @event.operationStatus "PARTIAL")}}
            <span class="flex items-center gap-1 text-yellow-600 font-medium text-sm">
              <div class="shrink-0 w-4 h-4">{{svgJar "caution-triangle" height="14" width="14"}}</div>
              Partial
            </span>
          {{else}}
            <span class="flex items-center gap-1 text-green-700 font-medium text-sm">
              <div class="shrink-0 w-4 h-4">{{svgJar "checkmark-circle-green" height="14" width="14"}}</div>
              Success
            </span>
          {{/if}}
        </div>
        <div class="flex-1 bg-white border border-gray-200 rounded-lg px-4 py-3">
          <p class="text-xs text-carbon-40 mb-1">Duration</p>
          <p class="text-sm text-carbon-70 mb-0">{{@event.durationDisplay}}</p>
        </div>
      </div>
    </div>

    {{! Failure Details — grey background section }}
    {{#if (eq @event.operationStatus "FAILED")}}
      <div class="bg-gray-50 px-6 py-4 border-t border-gray-200">
        <p class="text-sm font-bold text-carbon-50 mb-3">Failure details</p>
        <div class="bg-white border border-gray-200 rounded-lg overflow-hidden">
          {{! Recommended Action — prominent blue banner with padding }}
          {{#if @event.recommendedAction}}
            <div class="p-3">
              <div class="bg-gem-5 rounded-lg px-4 py-3">
                <p class="text-xs font-semibold text-gem-60 uppercase tracking-wide mb-1">Recommended Action</p>
                <p class="text-sm text-carbon-70 mb-0">{{@event.recommendedAction}}</p>
              </div>
            </div>
          {{/if}}
          {{! Reason }}
          {{#if @event.failureReason}}
            <div class="px-4 py-3 border-t border-gray-100">
              <p class="text-xs text-carbon-40 mb-0.5">Reason</p>
              <p class="text-sm text-carbon-70 mb-0">{{@event.failureReason}}</p>
            </div>
          {{/if}}
          {{! Failure Type }}
          {{#if @event.failureType}}
            <div class="px-4 py-3 border-t border-gray-100">
              <p class="text-xs text-carbon-40 mb-0.5">Failure Type</p>
              <p class="text-sm text-carbon-70 mb-0">{{@event.failureType}}</p>
            </div>
          {{/if}}
          {{! Integration + View docs }}
          <div class="px-4 py-3 border-t border-gray-100">
            <p class="text-xs text-carbon-40 mb-0.5">Integration</p>
            <div class="flex items-center gap-3">
              <span class="text-sm text-carbon-70">{{@event.integrationName}}</span>
              {{#if @event.helpCenterUrl}}
                <a
                  href={{@event.helpCenterUrl}}
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-sm text-gem-60 hover:underline font-medium"
                >
                  View docs
                </a>
              {{/if}}
            </div>
          </div>
        </div>
      </div>
    {{/if}}

    {{! Operation — grey background section }}
    <div class="bg-gray-50 px-6 py-4 border-t border-gray-200">
      <p class="text-sm font-bold text-carbon-50 mb-3">Operation</p>
      <div class="bg-white border border-gray-200 rounded-lg">
        <div class="px-4 py-3">
          <p class="text-xs text-carbon-40 mb-0.5">Type</p>
          <p class="text-sm text-carbon-70 mb-0">{{@event.operationTypeLabel}}</p>
        </div>
        <div class="px-4 py-3 border-t border-gray-100">
          <p class="text-xs text-carbon-40 mb-0.5">Direction</p>
          <p class="text-sm text-carbon-70 mb-0">{{@event.direction}}</p>
        </div>
        {{#if @event.operationDefinition}}
          <div class="px-4 py-3 border-t border-gray-100">
            <p class="text-xs text-carbon-40 mb-0.5">Definition</p>
            <p class="text-sm text-carbon-70 mb-0">{{@event.operationDefinition}}</p>
          </div>
        {{/if}}
        {{#if @event.endpoint}}
          <div class="px-4 py-3 border-t border-gray-100">
            <p class="text-xs text-carbon-40 mb-0.5">Endpoint</p>
            <p class="text-sm text-carbon-70 mb-0 font-mono break-all">{{@event.endpoint}}</p>
          </div>
        {{/if}}
        {{#if @event.primaryMetric}}
          <div class="px-4 py-3 border-t border-gray-100">
            <p class="text-xs text-carbon-40 mb-0.5">Primary metric</p>
            <p class="text-sm text-carbon-70 mb-0">{{@event.primaryMetric}}</p>
          </div>
        {{/if}}
        {{#if @event.contextDetails}}
          {{#each-in @event.contextDetails as |label value|}}
            <div class="px-4 py-3 border-t border-gray-100">
              <p class="text-xs text-carbon-40 mb-0.5">{{label}}</p>
              {{#if (and (eq label "Actor") @eventLogUrl)}}
                <div class="flex items-center gap-2 text-sm text-carbon-70">
                  <span>{{value}}</span>
                  <span class="text-carbon-30">|</span>
                  <a href={{@eventLogUrl}} class="text-gem-60 hover:underline font-medium">View event log</a>
                </div>
              {{else}}
                <p class="text-sm text-carbon-70 mb-0">{{value}}</p>
              {{/if}}
            </div>
          {{/each-in}}
        {{/if}}
        <div class="px-4 py-3 border-t border-gray-100">
          <p class="text-xs text-carbon-40 mb-0.5">Event ID</p>
          <p class="text-xs text-carbon-70 mb-0 font-mono break-all">{{@event.eventId}}</p>
        </div>
      </div>
    </div>

    {{! Related Events — grey background section }}
    <div class="bg-gray-50 px-6 py-4 pb-20 border-t border-gray-200">
      <p class="text-sm font-bold text-carbon-50 mb-3">Related events</p>
      <div class="bg-white border border-gray-200 rounded-lg">
        {{#if @event.correlationId}}
          <div class="px-4 py-3">
            <p class="text-xs text-carbon-40 mb-0.5">Correlation ID</p>
            <p class="text-xs text-carbon-70 mb-0 font-mono break-all">{{@event.correlationId}}</p>
          </div>
        {{/if}}
        <div class="px-4 py-3 {{if @event.correlationId 'border-t border-gray-100'}}">
          {{#if @relatedEventsLoading}}
            <p class="text-xs text-carbon-40 mb-0">Loading…</p>
          {{else if (gt @relatedEventsCount 0)}}
            <button
              type="button"
              class="flex items-center justify-between w-full text-left hover:bg-gray-50 -mx-4 -my-3 px-4 py-3 rounded-b-lg"
              {{on "click" @onOpenRelatedDrawer}}
            >
              <span class="text-sm text-carbon-70">{{@relatedEventsCount}}
                related event{{if (gt @relatedEventsCount 1) "s"}}</span>
              {{svgJar "chevron-grey-right" height="16" width="16" class="shrink-0 text-carbon-40"}}
            </button>
          {{else}}
            <p class="text-xs text-carbon-40 mb-0">No related events</p>
          {{/if}}
        </div>
      </div>
    </div>
  </TableDrawer>
</template> satisfies TOC<HealthDashboardEventDetailDrawerSignature>;
